import { Ionicons } from "@expo/vector-icons";
import { camelCase, capitalCase, constantCase } from "change-case";
import { useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Text, FlatList } from "react-native";
import { ActivityIndicator } from "react-native-paper";
import Toast from "react-native-toast-message";

import RenderFields from "./utils/RenderFields";

import ButtonSmall from "@/components/button/ButtonSmall";
import ButtonUploadDoc from "@/components/button/ButtonUploadDoc";
import {
  BridgeRequirements,
  DocumentRequirements,
  Application,
} from "@/screens/home/userpanel/UserPanel";
import { sendRequest } from "@/utils/ApiUtil";
import { toastInstance } from "@/utils/ToastConfig";
import {
  allOnboardingDocuments,
  corporatePdfDocuments,
  validateAllFields,
} from "@/utils/onboarding/OnboardingUtils";

interface UpdateApplicationProps {
  requirements: BridgeRequirements;
  applicationId: string;
  applicationType: Application;
  hasSubmitted: () => void;
}

interface RequestData {
  applicationId: string;
  corporation?: {
    kycProfile?: {
      [key: string]: string;
    };
    investmentProfile?: {
      [key: string]: string;
    };
    registeredAddress?: {
      [key: string]: string;
    };
    mailingAddress?: {
      [key: string]: string;
    };
    individuals?: any[];
    documents?: any[];
  };
  individual?: {
    kycProfile?: {
      [key: string]: string;
    };
    investmentProfile?: {
      [key: string]: string;
    };
    mailingAddress?: {
      [key: string]: string;
    };
    documents?: any[];
  };
}

export default function UpdateApplication(props: UpdateApplicationProps) {
  const [fields, setFields] = useState<string[]>([]);
  const [documents, setDocuments] = useState<DocumentRequirements[]>([]);
  const [individualsFields, setIndividualsFields] = useState<string[]>([]);
  const [dataToRequest, setDataToRequest] = useState<any>({});
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const booleanEquivalence = (value: string) => {
    if (value === "YES") return true;
    else if (value === "NO") return false;
    //Formatting date to be in the format of "YYYY-MM-DD"
    else if (value.includes("/")) return value.replaceAll("/", "-");
    else return value;
  };

  useFocusEffect(
    React.useCallback(() => {
      setFields([]);
      setIndividualsFields([]);
      setDocuments([]);
      setDataToRequest({});
      setSubmitting(false);
      fetchApplication();
    }, [])
  );

  //Take the string as "corporation.missingFieldExample" format and return "fieldExample".
  function processMissingFields(fields: string[]): string[] {
    return fields.map((field) => {
      const fieldSplit = field.split(".");
      return camelCase(fieldSplit[fieldSplit.length - 1]);
    });
  }

  const fetchApplication = () => {
    try {
      const response = props.requirements;
      const documentsArray: DocumentRequirements[] = [];
      const individualFieldsArray: string[] = [];
      const fieldsArray: string[] = [];

      if (response.missingFields && response.missingFields.length > 0) {
        const missingFields = processMissingFields(response.missingFields);
        missingFields.forEach((field) => {
          if (!allOnboardingDocuments.includes(constantCase(field))) {
            fieldsArray.push(field);
          }
        });
      }

      if (response.missingDocuments) {
        documentsArray.push(...response.missingDocuments);
      }

      if (response.missingIndividualDetails) {
        response.missingIndividualDetails.forEach((individual) => {
          if (individual.missingDocuments) {
            individual.missingDocuments.forEach((document) => {
              const individualDocumentObject = {
                document: `INDIVIDUAL,${
                  individual.firstname || individual.firstName
                },${individual.id},${document}`,
              };
              documentsArray.push(individualDocumentObject);
            });
          }
          if (individual.missingFields) {
            const individualFieldsToProcess = processMissingFields(
              individual.missingFields
            );
            individualFieldsToProcess.forEach((field) => {
              const individualField = `INDIVIDUAL_${
                individual.firstname || individual.firstName
              }_${individual.id}_${field}`;
              individualFieldsArray.push(individualField);
            });
          }
        });
      }

      setFields(fieldsArray);
      setDocuments(documentsArray);
      setIndividualsFields(individualFieldsArray);
    } catch (error) {
      console.log(error);
    }
  };

  const submitApplication = async (data: any) => {
    setSubmitting(true);
    const applicationData = props.requirements;
    const requestData: RequestData = {
      applicationId: props.applicationId,
    };
    if (applicationData.missingIndividualDetails) {
      requestData.corporation = {
        ...requestData.corporation,
        individuals: [],
      };
    }
    const possibleNestedObjects = [
      "mailingAddress",
      "registeredAddress",
      "kycProfile",
      "investmentProfile",
    ];

    // If the field is a string, add it to the request data in the appropriate object
    if (applicationData.missingFields) {
      applicationData.missingFields
        .filter(
          (item) =>
            !allOnboardingDocuments.includes(
              item.split(".")[item.split(".").length - 1]
            )
        )
        .forEach((field) => {
          const splitField = field.split(".");
          const rootKey = splitField[0];
          const nestedKey = splitField[1];
          const fieldKey = splitField[splitField.length - 1];

          // If it's a nested object
          if (nestedKey && possibleNestedObjects.includes(nestedKey)) {
            requestData[rootKey] = {
              ...requestData[rootKey],
              [nestedKey]: {
                ...requestData[rootKey]?.[nestedKey],
                [fieldKey]: booleanEquivalence(data[fieldKey]),
              },
            };
          }
          // If it's a regular field
          else {
            requestData[rootKey] = {
              ...requestData[rootKey],
              [fieldKey]: booleanEquivalence(data[fieldKey]),
            };
          }
        });
    }

    // If the field is a document, add it to the request data in the appropriate object
    if (applicationData.missingDocuments) {
      applicationData.missingDocuments.forEach((documentObject) => {
        if (data[documentObject.document]) {
          if (props.applicationType === "CORPORATION") {
            requestData.corporation = {
              ...requestData.corporation,
              documents: [
                ...(requestData.corporation?.documents || []),
                {
                  type: documentObject.document,
                  content: data[documentObject.document],
                },
              ],
            };
          } else {
            requestData.individual = {
              ...requestData.individual,
              documents: [
                ...(requestData.individual?.documents || []),
                {
                  type: documentObject.document,
                  content: data[documentObject.document],
                },
              ],
            };
          }
        }
      });
    }
    // If the field is from corporate individual, add it to the request data in the appropriate object
    if (applicationData.missingIndividualDetails) {
      applicationData.missingIndividualDetails.forEach((individual) => {
        //Object to store individual data (id, firstName, missingField, documents)
        const individualObject: any = {};
        individualObject.id = individual.id;
        individualObject.firstName = individual.firstname;

        if (individual.missingFields) {
          individual.missingFields.forEach((field) => {
            if (data[`${individual.id}_${field}`]) {
              individualObject[field] = booleanEquivalence(
                data[`${individual.id}_${field}`]
              );
            }
          });
        }

        if (individual.missingDocuments) {
          individual.missingDocuments.forEach((document) => {
            if (data[`${individual.id}_${document}`]) {
              individualObject.documents = [
                ...(individualObject.documents || []),
                {
                  type: document,
                  content: data[`${individual.id}_${document}`],
                },
              ];
            }
          });
        }

        requestData.corporation?.individuals?.push(individualObject);
      });
    }

    try {
      const response = await sendRequest(
        "POST",
        "/bridge/activate",
        requestData,
        true
      );
      if (response) {
        toastInstance("success", t("common:toast.subtitle.userdataupdated"));
        props.hasSubmitted();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  //Individual documents has the name in 'INDIVIDUAL_firstname_id_document' format
  const renderDocumentsToUpload = (documents: DocumentRequirements[]) => {
    return (
      <FlatList
        data={documents}
        className="w-full items-center p-2"
        renderItem={({ item }) => (
          <View className="w-full flex-row justify-between items-center mt-3 p-0.5 lg:gap-48 ">
            <Text className="text-lg font-light">
              {item.document.startsWith("INDIVIDUAL")
                ? `${capitalCase(item.document.split(",")[3])} (${
                    item.document.split(",")[1]
                  })`
                : capitalCase(item.document)}
            </Text>
            {dataToRequest[item.document] ||
            dataToRequest[
              `${item.document.split(",")[2]}_${item.document.split(",")[3]}`
            ] ? (
              <View className="w-40 items-end">
                <Ionicons name="checkmark-circle" color="green" size={32} />
              </View>
            ) : (
              <ButtonUploadDoc
                label={t("common:button.upload")}
                onClick={(data) => {
                  let document: string;
                  if (item.document.startsWith("INDIVIDUAL")) {
                    const documentName = item.document.split(",");
                    document = `${documentName[2]}_${documentName[3]}`;
                  } else {
                    document = item.document;
                  }
                  setDataToRequest((prevFields) => ({
                    ...prevFields,
                    [document]: data.uri,
                  }));
                }}
                item={item.document}
                size={10000000}
                sizeMessage={t("common:toast.subtitle.filetoolarge10mb")}
                onlyImage={
                  item.document.startsWith("INDIVIDUAL")
                    ? !corporatePdfDocuments.includes(
                        item.document.split(",")[3]
                      )
                    : !corporatePdfDocuments.includes(item.document)
                }
              />
            )}
          </View>
        )}
      />
    );
  };

  return (
    <View className="bg-white1 w-full rounded-lg shadow">
      <View className="w-full p-2 bg-purple3 rounded-tl-lg rounded-tr-lg">
        <Text className="text-white1 text-xl font-light">
          {t("home/userpanel:sections.updateapplication.title")}
        </Text>
      </View>
      {props.requirements.status?.toLowerCase() === "created" ? (
        <View className="w-full items-center p-3 gap-2">
          <Text className="text-lg text-midgray">
            {t("home/userpanel:sections.updateapplication.created")}
          </Text>
          <ActivityIndicator size="large" color="#5D0CBA" />
        </View>
      ) : (
        <View className="w-full items-center p-4 z-50">
          <Text className="text-lg text-center">
            {t("home/userpanel:sections.updateapplication.subtitle")}
          </Text>
          <View className="w-full items-center mt-4 z-50">
            {documents.length > 0 && (
              <View className="w-full items-center gap-2 mt-4">
                <Text className="text-lg font-medium text-darkergray">
                  {t("common:terms.documents")}
                </Text>
                {renderDocumentsToUpload(documents)}
              </View>
            )}
            {fields.length > 0 && (
              <View className="w-full items-center gap-3 mt-4 z-50">
                <Text className="text-lg font-medium text-darkergray">
                  {t("common:terms.generalinformation")}
                </Text>
                <RenderFields
                  requirements={props.requirements}
                  fields={fields}
                  fieldValue={(field, value) => {
                    setDataToRequest((prevFields) => ({
                      ...prevFields,
                      [field]: value,
                    }));
                  }}
                />
              </View>
            )}
            {individualsFields.length > 0 && (
              <View className="w-full items-center gap-2 mt-4 z-40">
                <Text className="text-lg font-medium text-darkergray">
                  {t("common:terms.corporateindividuals")}
                </Text>
                <RenderFields
                  requirements={props.requirements}
                  fields={individualsFields}
                  fieldValue={(field, value) => {
                    setDataToRequest((prevFields) => ({
                      ...prevFields,
                      [field]: value,
                    }));
                  }}
                />
              </View>
            )}
          </View>
          <View className="my-2 z-40">
            <ButtonSmall
              label={t("common:button.confirm")}
              onClick={() => {
                const validateData = validateAllFields(
                  props.requirements,
                  dataToRequest
                );
                if (!validateData) {
                  Toast.show({
                    type: "info",
                    visibilityTime: 5000,
                    props: {
                      text1: t("common:toast.warning"),
                      text2: t("common:toast.subtitle.formnotfillentirely"),
                    },
                  });
                } else {
                  Toast.hide();
                  submitApplication(dataToRequest);
                }
              }}
              isLoading={submitting}
            />
          </View>
        </View>
      )}
    </View>
  );
}
